var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/** Field identity as the rest of the stack understands it: `fieldId`, else tag. */
export var fieldIdentity = function (field) { var _a; return (_a = field.fieldId) !== null && _a !== void 0 ? _a : field.tag; };
/** Every field identity present anywhere in the document (all tabs). */
export var presentFieldIdentities = function (config) {
    var _a, _b;
    var ids = new Set();
    for (var _i = 0, _c = (_a = config === null || config === void 0 ? void 0 : config.tabs) !== null && _a !== void 0 ? _a : []; _i < _c.length; _i++) {
        var tab = _c[_i];
        for (var _d = 0, _e = (_b = tab.fields) !== null && _b !== void 0 ? _b : []; _d < _e.length; _d++) {
            var field = _e[_d];
            ids.add(fieldIdentity(field));
        }
    }
    return ids;
};
/** JSON deep clone. The documents are plain JSON by construction (schema-validated). */
var clone = function (value) { return JSON.parse(JSON.stringify(value)); };
/**
 * All fields carrying `tag` in the packaged template, in document order.
 * Order matters: it is the order NK sees them in on the form, so it is the
 * order the picker should list them in.
 */
export var collectPackagedVariants = function (packaged, tag) {
    var _a, _b;
    var out = [];
    for (var _i = 0, _c = (_a = packaged === null || packaged === void 0 ? void 0 : packaged.tabs) !== null && _a !== void 0 ? _a : []; _i < _c.length; _i++) {
        var tab = _c[_i];
        for (var _d = 0, _e = (_b = tab.fields) !== null && _b !== void 0 ? _b : []; _d < _e.length; _d++) {
            var field = _e[_d];
            if (field.tag === tag)
                out.push(field);
        }
    }
    return out;
};
/**
 * A field built from the MARC catalog entry for `tag`.
 *
 * Subfield selection is `default_visible || mandatory` — the meaningful set.
 * Copying every subfield MARC 21 allows would drop ~20 rows into the editor
 * for a tag like 100, which none of the packaged templates do either.
 *
 * No `label` on the field or its subfields (see the module docblock), and no
 * `indicator*Def` (the editor reads those from the catalog live).
 */
export var catalogFieldFor = function (catalog, tag) {
    var _a, _b;
    var entry = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.fields) === null || _a === void 0 ? void 0 : _a[tag];
    if (!entry)
        return null;
    // Widened on purpose: the generated type is a non-empty tuple (`minItems: 1`),
    // and `tuple | never[]` makes `.filter` resolve to `never` under the build config.
    var catalogSubfields = (_b = entry.subfields) !== null && _b !== void 0 ? _b : [];
    var subfields = catalogSubfields
        .filter(function (sf) { return sf.default_visible === true || sf.mandatory === true; })
        .map(function (sf) {
        var out = { code: sf.code, defaultVisible: sf.default_visible === true };
        if (sf.mandatory === true)
            out.required = true;
        return out;
    });
    var field = { tag: tag, subfields: subfields };
    if (entry.repeatable_default === true)
        field.repeatable = true;
    return field;
};
/**
 * Last-resort shape for a tag the catalog does not know (custom / extension
 * tags — the catalog is closed over MARC 21 authority). One `$a`, visible, no
 * invented label.
 */
export var bareFieldFor = function (tag) { return ({
    tag: tag,
    subfields: [{ code: 'a', defaultVisible: true }],
}); };
/**
 * The candidate definitions for adding `tag`, best first, with exactly one
 * fallback option at the end so the admin can always choose a blank field.
 *
 * Returns `[]` for a blank tag — the caller treats that as "nothing to add",
 * which is the pre-existing behaviour of the empty-tag guard.
 */
export var resolveNewFieldVariants = function (_a) {
    var tag = _a.tag, packaged = _a.packaged, config = _a.config, catalog = _a.catalog;
    var trimmed = tag.trim();
    if (!trimmed)
        return [];
    var present = presentFieldIdentities(config);
    var variants = collectPackagedVariants(packaged, trimmed).map(function (field, idx) {
        var _a, _b;
        var identity = fieldIdentity(field);
        var packagedLabel = typeof field.label === 'string'
            ? field.label
            : (_a = field.label) === null || _a === void 0 ? void 0 : _a.cs;
        return {
            key: "packaged:".concat((_b = field.fieldId) !== null && _b !== void 0 ? _b : "".concat(trimmed, "#").concat(idx)),
            origin: 'PACKAGED',
            field: clone(field),
            fieldId: field.fieldId,
            packagedLabel: packagedLabel,
            alreadyPresent: present.has(identity),
        };
    });
    var catalogField = catalogFieldFor(catalog, trimmed);
    variants.push(catalogField
        ? { key: 'catalog', origin: 'CATALOG', field: catalogField, alreadyPresent: false }
        : { key: 'bare', origin: 'BARE', field: bareFieldFor(trimmed), alreadyPresent: false });
    return variants;
};
/**
 * Which variant the dialog should pre-select: the first one that would not
 * duplicate an existing identity. Since the fallback is never `alreadyPresent`
 * this always finds something, but the `?? null` keeps the signature honest for
 * an empty list (blank tag).
 */
export var pickDefaultVariant = function (variants) { var _a; return (_a = variants.find(function (v) { return !v.alreadyPresent; })) !== null && _a !== void 0 ? _a : null; };
/**
 * Properties that only mean something on a picker, so they must not outlive the
 * type the picker points at. `multiValue` and `multiValueComponentType` are in
 * the list because `getInputKind` reads a `multiValue` subfield with no type
 * back as the free-text chip input — a control neither source asked for.
 */
var PICKER_ONLY_KEYS = [
    'inputType', 'allowFreeText', 'literalFreeText', 'pickerPreload', 'vocabularyDisplayFormat',
    'alsoRecordTypes', 'multiValue', 'multiValueComponentType',
];
/**
 * Flags that are only coherent when the subfield also carries a `defaultValue`
 * to stand in for the input they suppress. Losing the value has to take them
 * with it; see `repairDependentProperties`.
 */
var VALUE_DEPENDENT_KEYS = ['hidden', 'readOnly'];
/**
 * Undo the collateral damage of an intersection: one property can depend on
 * another, so dropping the second leaves the first not weaker but *broken*, and
 * a broken insert is worse than a plain one because the admin has to notice it
 * before they can fix it.
 *
 *  - **`hidden` and `readOnly` each need a `defaultValue`** — the pair in
 *    `VALUE_DEPENDENT_KEYS`. Hidden with nothing to submit means the renderer
 *    drops the subfield from the form entirely (CZNA-246), and `SubfieldEditor`
 *    refuses to let the checkbox create that state. `readOnly` is the visible
 *    twin: "displayed but not editable" with no value to display is a box the
 *    cataloguer can see, cannot fill, and cannot get rid of. Real case —
 *    `conspect-group.json` `590 $w` is `readOnly` in both variants with
 *    `defaultValue` `g`/`h`, so the direction is dropped and, before this,
 *    `readOnly` was left standing over nothing. All 23 `readOnly` subfields in
 *    the shipped templates carry a `defaultValue`, which is the evidence that
 *    this is a pair and not a coincidence.
 *  - **a picker needs something to pick from.** `getInputKind` maps a
 *    `record-picker` carrying neither `recordType` nor `vocabularyType` back to
 *    the `authority` kind with a blank target select — the trap
 *    `DEFAULT_VOCABULARY_TYPE` exists to avoid. When the type disagreed (`368`
 *    `$d` points at four different vocabularies), the picker goes with it and
 *    the subfield arrives as plain text for the admin to set deliberately.
 *
 *    The trigger is the lost TYPE, deliberately not "`inputType` still looks
 *    like a picker": `inputType` can itself be dropped, and then a surviving
 *    `multiValue` reads back as the free-text chip input — a third control
 *    neither variant asked for. Two multi-pickers over different targets
 *    (`multi-record-picker`/`PERSONAL` vs `multi-vocabulary`/`VOCABULARY_*`)
 *    intersect to exactly that. Conversely a subfield that never had a type is
 *    left alone: `multiValue` with no picker is the legitimate free-text chip
 *    list (TASK-110, `100 $c`), and `lostItsType` is what tells the two apart.
 *
 * What decides it is `dropped` — a dependency THIS function's caller took away —
 * and never the mere absence of one in the result. The shipped templates carry
 * 30 subfields that are `hidden` with no `defaultValue` on purpose (`$7`, the
 * ontology link, is hidden and filled by the system rather than by a default)
 * and two typeless pickers. Repairing on absence alone would quietly rewrite
 * that convention, and would make the same `550` `$7` resolve two different
 * ways depending on whether the edited field happened to carry a `fieldId`:
 * verbatim on the owned path, un-hidden on the borrowed one.
 */
var repairDependentProperties = function (out, dropped) {
    if (dropped.has('defaultValue')) {
        for (var _i = 0, VALUE_DEPENDENT_KEYS_1 = VALUE_DEPENDENT_KEYS; _i < VALUE_DEPENDENT_KEYS_1.length; _i++) {
            var key = VALUE_DEPENDENT_KEYS_1[_i];
            if (out[key] === true)
                delete out[key];
        }
    }
    var lostItsType = dropped.has('recordType') || dropped.has('vocabularyType');
    if (lostItsType && !out.recordType && !out.vocabularyType) {
        for (var _a = 0, PICKER_ONLY_KEYS_1 = PICKER_ONLY_KEYS; _a < PICKER_ONLY_KEYS_1.length; _a++) {
            var key = PICKER_ONLY_KEYS_1[_a];
            delete out[key];
        }
    }
    return out;
};
/**
 * Properties on which `defs` all agree, dropped otherwise, then repaired.
 * `code` always survives (the caller filtered on it) and `label` / `labelKey`
 * never do — a borrowed name belongs to the variant that owns the subfield.
 *
 * Disagreement is measured over the UNION of the defs' keys, not the first
 * def's: a property only one variant carries is a disagreement too, and reading
 * `Object.keys(first)` alone would let it through whenever `first` is the
 * variant that happens to declare it.
 *
 * Compared by JSON serialisation, so the nested `components` / `selectOptions`
 * arrays are handled without a deep-equal dependency; they are plain JSON by
 * construction, the same assumption `clone` makes. That comparison is
 * key-ORDER sensitive, so two variants that write the same `actionButton` with
 * its keys in a different order read as disagreeing and the property is
 * dropped. It fails in the safe direction — dropping, never inventing — and no
 * shipped template does it, which is why a deep-equal is not worth pulling in.
 *
 * Only `defaultVisible` gets restored afterwards (see `withExplicitVisibility`),
 * because it is the only property whose absence asserts something. `required`
 * is the near-miss worth naming: when the variants of a tag disagree on it
 * (`subject.json`'s three `670`s did until CZNA-1659 marked `$a` required on
 * all of them), the borrow drops it and the catalog is not consulted to put it
 * back. That is deliberate — losing `required` makes the form less strict,
 * never broken, and inventing a constraint the admin did not ask for is the
 * worse failure.
 *
 * `defs` must be non-empty, and is never mutated.
 */
export var intersectSubfieldDefs = function (defs) {
    var first = defs[0], rest = defs.slice(1);
    var out = clone(first);
    var dropped = new Set();
    var keys = new Set();
    for (var _i = 0, defs_1 = defs; _i < defs_1.length; _i++) {
        var def = defs_1[_i];
        for (var _a = 0, _b = Object.keys(def); _a < _b.length; _a++) {
            var key = _b[_a];
            keys.add(key);
        }
    }
    var _loop_1 = function (key) {
        if (key === 'label' || key === 'labelKey') {
            delete out[key];
        }
        else if (key !== 'code') {
            var mine_1 = JSON.stringify(out[key]);
            if (!rest.every(function (d) { return JSON.stringify(d[key]) === mine_1; })) {
                if (key in out)
                    delete out[key];
                dropped.add(key);
            }
        }
    };
    // `Array.from`, not `for…of` over the Set: the production stripes build
    // compiles without `downlevelIteration` and at a pre-ES2015 target, so
    // iterating a Set directly is TS2802 there — and `tsc --noEmit -p .` does NOT
    // see it, because the module's own tsconfig is the permissive one. The seven
    // TS7053s that followed were fallout: once TS2802 fires, `key` degrades to
    // `any` and every `out[key]` becomes an implicit-any index.
    for (var _c = 0, _d = Array.from(keys); _c < _d.length; _c++) {
        var key = _d[_c];
        _loop_1(key);
    }
    return repairDependentProperties(out, dropped);
};
/**
 * The packaged definitions of subfield `code` on tag `fieldTag`, one per
 * variant of the tag that actually declares it. A variant without the subfield
 * (`550-related` has no `$w`) contributes nothing rather than an absence —
 * "this relationship type has no `$w`" is not evidence about what `$w` looks
 * like where it does exist.
 */
export var collectPackagedSubfieldDefs = function (packaged, fieldTag, code) {
    var _a;
    var out = [];
    for (var _i = 0, _b = collectPackagedVariants(packaged, fieldTag); _i < _b.length; _i++) {
        var field = _b[_i];
        var match = ((_a = field.subfields) !== null && _a !== void 0 ? _a : []).find(function (sf) { return sf.code === code; });
        if (match)
            out.push({ fieldId: field.fieldId, subfield: match });
    }
    return out;
};
/**
 * A subfield built from the live MARC catalog entry for `(fieldTag, code)`.
 *
 * Mirrors `catalogFieldFor`'s per-subfield shape exactly — `defaultVisible`
 * from `default_visible`, `required` only when `mandatory`, and no label — so
 * a `$u` inherited via the catalog is the same row whether it arrived with a
 * whole new 670 or was added to one by hand.
 */
export var catalogSubfieldFor = function (catalog, fieldTag, code) {
    var _a, _b, _c;
    var entry = (_c = (_b = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.fields) === null || _a === void 0 ? void 0 : _a[fieldTag]) === null || _b === void 0 ? void 0 : _b.subfields) === null || _c === void 0 ? void 0 : _c.find(function (sf) { return sf.code === code; });
    if (!entry)
        return null;
    var out = { code: code, defaultVisible: entry.default_visible === true };
    if (entry.mandatory === true)
        out.required = true;
    return out;
};
/**
 * Last-resort shape: visible, no default, and — the point of the ticket — no
 * invented `label`.
 */
export var bareSubfieldFor = function (code) { return ({
    code: code,
    defaultVisible: true,
}); };
/**
 * Put an explicit `defaultVisible` back on a definition the intersection left
 * without one.
 *
 * `defaultVisible` is the one property whose *absence* is not "unknown" but an
 * assertion: `DynamicFieldPanel` renders a subfield when
 * `defaultVisible !== false`, so dropping it to disagreement silently votes
 * VISIBLE — the "empty box the cataloguer should not see" this ticket is about.
 * Every other producer here (a verbatim packaged copy, the catalog, the bare
 * fallback) always emits one; the intersection was the only path that could
 * omit it. Real case: `024 $2` has seven variants in `corporate.json` that
 * disagree on nearly everything, and the borrow path resolved it to `{code}`
 * alone.
 *
 * The catalog answers it when it knows the subfield, because that is the same
 * source the CATALOG path would have used. Failing that it is `false` — behind
 * the expand toggle, where an admin who wanted it can reveal it in one click,
 * rather than in a cataloguer's face by default. Unlike `hidden`, this is not
 * a trap either way: the subfield stays fillable.
 *
 * `wasDropped` keeps this on the same footing as the other two repairs: fill in
 * only what the intersection took away. If NO variant stated a visibility, the
 * template is saying "visible" in its own idiom and the borrowed copy has to
 * say the same thing the owned copy would — otherwise the same subfield comes
 * out visible when the field carries a `fieldId` and hidden-behind-the-toggle
 * when it does not. No shipped subfield omits `defaultVisible` today, so this
 * is latent; it is written this way so the invariant does not depend on that.
 */
var withExplicitVisibility = function (subfield, catalog, fieldTag, wasDropped) {
    if (subfield.defaultVisible !== undefined || !wasDropped)
        return subfield;
    var fromCatalog = catalogSubfieldFor(catalog, fieldTag, subfield.code);
    return __assign(__assign({}, subfield), { defaultVisible: (fromCatalog === null || fromCatalog === void 0 ? void 0 : fromCatalog.defaultVisible) === true });
};
/**
 * The definition a newly added subfield starts from — the identified packaged
 * variant's own, else what the tag's other definitions agree on, else the MARC
 * catalog, else bare. Never null except for a blank code, which the caller
 * already treats as "nothing to add".
 */
export var resolveNewSubfield = function (_a) {
    var code = _a.code, fieldTag = _a.fieldTag, fieldId = _a.fieldId, packaged = _a.packaged, catalog = _a.catalog;
    var trimmed = code.trim();
    if (!trimmed)
        return null;
    var packagedDefs = collectPackagedSubfieldDefs(packaged, fieldTag, trimmed);
    // The edited field IS this packaged variant, and the variant declares the
    // subfield → copy it verbatim, template label included. That label is the
    // template's own, one of the names the EFC compiler defers to; the ban is on
    // inventing one, and on borrowing a sibling's.
    var owned = fieldId
        ? packagedDefs.find(function (d) { return d.fieldId === fieldId; })
        : undefined;
    if (owned) {
        return { origin: 'PACKAGED', subfield: clone(owned.subfield) };
    }
    if (packagedDefs.length) {
        // "Dropped" for `defaultVisible` = some variant stated one and the
        // intersection could not keep it. Computed here rather than threaded out of
        // `intersectSubfieldDefs`, whose contract is a plain `SubfieldDef`.
        var someStatedVisibility = packagedDefs
            .some(function (d) { return d.subfield.defaultVisible !== undefined; });
        return {
            origin: 'PACKAGED_TAG',
            subfield: withExplicitVisibility(intersectSubfieldDefs(packagedDefs.map(function (d) { return d.subfield; })), catalog, fieldTag, someStatedVisibility),
        };
    }
    var fromCatalog = catalogSubfieldFor(catalog, fieldTag, trimmed);
    if (fromCatalog)
        return { origin: 'CATALOG', subfield: fromCatalog };
    return { origin: 'BARE', subfield: bareSubfieldFor(trimmed) };
};
